import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../i18n/config";
import {
  Table,
  Container,
  Row,
  Col,
  Modal,
  Button as BootstrapButton,
  Form,
} from "react-bootstrap";
import "./patients.css";
import GetData from "../../services/getData";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import userIcon from "../../media/icons-user.png";
import filterIcon from "../../media/filter_icon.png";

const normalizeLogValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

function Patients() {
  const { t, i18n } = useTranslation("patients");
  const locale = getLocale(i18n.language);
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: "all", doctorIds: [] });
  const [isDoctorFilterOpen, setIsDoctorFilterOpen] = useState(false);
  const doctorFilterRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const searchInputRef = useRef(null);

  // === Модальное окно ===
  const [showModal, setShowModal] = useState(false);
  const [newPatient, setNewPatient] = useState({
    fio: "",
    mail: "",
    phone: "",
    diagnosis: "",
    doctorIds: [],
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  const loadPatientsList = async () => {
    const preserveSearchFocus =
      searchInputRef.current === document.activeElement;
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "patient",
        block: "list",
        status: filters.status === "all" ? "" : filters.status,
        fio: debouncedSearchTerm,
        doctorIds: JSON.stringify(filters.doctorIds || []),
        page: pagination.page,
        limit: pagination.limit,
      });

      setPatients(data.result || []);
      setDoctors(data.doctors || []);

      if (data.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: data.pagination.page ?? prev.page,
          limit: data.pagination.limit ?? prev.limit,
          total: data.pagination.total ?? prev.total,
          pages: data.pagination.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      console.error("Ошибка загрузки пациентов:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
      if (preserveSearchFocus && searchInputRef.current) {
        searchInputRef.current.focus({ preventScroll: true });
      }
    }
  };


  
  const handleAddClick = () => {
    setNewPatient({ fio: "", mail: "", phone: "", diagnosis: "", doctorIds: [] });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPatient((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewPatientDoctorToggle = (doctorId) => {
    const value = String(doctorId ?? "");
    setNewPatient((prev) => {
      const current = Array.isArray(prev.doctorIds) ? prev.doctorIds : [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, doctorIds: next };
    });
  };

  const newPatientDoctorNames = (newPatient.doctorIds || [])
    .map((id) => doctors.find((doc) => String(doc?.id) === String(id))?.name)
    .filter(Boolean);

  const handleSavePatient = async () => {
    if (!newPatient.fio.trim()) {
      alert(t("list.validation.needFio"));
      return;
    }
    if (!Array.isArray(newPatient.doctorIds) || newPatient.doctorIds.length === 0) {
      alert(t("list.validation.needDoctor"));
      return;
    }

    try {
      const response = await GetData({
        module: "patient",
        block: "list",
        action: "add",
        fio: newPatient.fio,
        mail: newPatient.mail,
        phone: newPatient.phone,
        diagnosis: newPatient.diagnosis,
        doctorId: newPatient.doctorIds[0],
        id_users: JSON.stringify(newPatient.doctorIds),
      });

      const createdPatientId =
        response?.new_id?.value ??
        response?.new_id ??
        response?.result?.new_id?.value ??
        response?.result?.new_id;
      await logPatientChange({
        idPatient: createdPatientId,
        text: buildPatientLogText("patient_created", {
          details: [
            `${t("log.fio")}: ${normalizeLogValue(newPatient.fio)}`,
            `${t("log.phone")}: ${normalizeLogValue(newPatient.phone)}`,
            `${t("log.email")}: ${normalizeLogValue(newPatient.mail)}`,
            `${t("log.diagnosis")}: ${normalizeLogValue(newPatient.diagnosis)}`,
            `${t("log.doctor")}: ${normalizeLogValue(newPatientDoctorNames.join(", "))}`,
          ].join("; "),
        }),
      });

      setShowModal(false);
      if (createdPatientId) {
        navigate(`/patient/${createdPatientId}/profile`);
        return;
      }

      loadPatientsList();
    } catch (err) {
      console.error("Ошибка добавления пациента:", err);
      alert(t("list.errors.addFailed"));
    }
  };

  useEffect(() => {
    document.title = t("list.title");
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadPatientsList();
  }, [filters.status, filters.doctorIds, debouncedSearchTerm, pagination.page]);

  // Закрытие выпадающего списка врачей по клику вне него.
  useEffect(() => {
    if (!isDoctorFilterOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        doctorFilterRef.current &&
        !doctorFilterRef.current.contains(event.target)
      ) {
        setIsDoctorFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDoctorFilterOpen]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, page: 1 })); // ← сброс
  };

  // Мультивыбор врачей в фильтре: переключаем id в массиве doctorIds.
  const handleDoctorFilterToggle = (doctorId) => {
    const value = String(doctorId ?? "");
    setFilters((prev) => {
      const current = Array.isArray(prev.doctorIds) ? prev.doctorIds : [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, doctorIds: next };
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const selectedDoctorNames = (filters.doctorIds || [])
    .map((id) => doctors.find((doc) => String(doc?.id) === String(id))?.name)
    .filter(Boolean);

  const formatDate = (input) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  };

  const renderPageButtons = () => {
    if (totalPages <= 1) {
      return null;
    }

    // Если страниц немного — показываем все
    if (totalPages < 5) {
      return Array.from({ length: totalPages }, (_, idx) => {
        const pageNum = idx + 1;
        return (
          <BootstrapButton
            key={pageNum}
            variant={pageNum === pagination.page ? "primary" : "outline-secondary"}
            size="sm"
            className="mx-1"
            onClick={() => handlePageClick(pageNum)}>
            {pageNum}
          </BootstrapButton>
        );
      });
    }

    // Если страниц много (5 и более) — показываем только 3 соседние страницы
    const currentPage = pagination.page;
    let start = Math.max(1, currentPage - 1);
    let end = start + 2;

    if (end > totalPages) {
      end = totalPages;
      start = end - 2;
    }

    const buttons = [];

    // Левое многоточие: переход к странице непосредственно перед первой отображаемой
    if (start > 1) {
      buttons.push(
        <BootstrapButton
          key="left-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(start - 1)}>
          ...
        </BootstrapButton>
      );
    }

    // Три ближайшие страницы
    for (let p = start; p <= end; p++) {
      buttons.push(
        <BootstrapButton
          key={p}
          variant={p === currentPage ? "primary" : "outline-secondary"}
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(p)}>
          {p}
        </BootstrapButton>
      );
    }

    // Правое многоточие: переход к странице сразу после последней отображаемой
    if (end < totalPages) {
      buttons.push(
        <BootstrapButton
          key="right-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(end + 1)}>
          ...
        </BootstrapButton>
      );
    }

    return buttons;
  };

  if (!hasLoadedOnce && loading) {
    return (
      <Container fluid="md" className="mt-4">
        {t("state.loading", { ns: "common" })}
      </Container>
    );
  }

  if (error && patients.length === 0) {
    return (
      <Container fluid="md" className="mt-4 text-danger">
        {t("error", { ns: "common", message: error })}
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={userIcon}
                title={t("list.title")}
                showStatus={false}
                status={""}
              />
            }
            buttons={[
              {
                label: t("list.add"),
                onClick: handleAddClick,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="page">
              <div className="page-content">
              <div className="page-block page-block--filters">
              <div className={`filters-panel ${isFiltersActive ? "active" : ""}`}>
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img className="title-with-icon__image" src={filterIcon} alt={t("filters.alt", { ns: "common" })} title={t("filters.alt", { ns: "common" })}/>
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <label>{t("filters.searchLabel", { ns: "common" })}</label>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <label>{t("list.statusLabel")}</label>
                      <select
                        value={filters.status}
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }>
                        <option value="all">{t("list.all")}</option>
                        <option value="Активный">{t("list.statusActive")}</option>
                        <option value="Неактивный">{t("list.statusInactive")}</option>
                      </select>
                    </div>
                    <div className="filter-group filter-group--multiselect" ref={doctorFilterRef}>
                      <label>{t("list.doctorLabel")}</label>
                      <button
                        type="button"
                        className="multiselect-toggle"
                        onClick={() => setIsDoctorFilterOpen((prev) => !prev)}
                      >
                        <span className="multiselect-toggle__text">
                          {selectedDoctorNames.length === 0
                            ? t("list.all")
                            : selectedDoctorNames.join(", ")}
                        </span>
                        <span className="multiselect-toggle__caret" aria-hidden="true">
                          ▾
                        </span>
                      </button>
                      {isDoctorFilterOpen && (
                        <div className="multiselect-dropdown">
                          <div className="multiselect-dropdown__list">
                            {doctors.length === 0 ? (
                              <div className="multiselect-dropdown__empty">
                                {t("list.doctorsEmpty")}
                              </div>
                            ) : (
                              doctors.map((doc) => (
                                <label
                                  className="multiselect-dropdown__item"
                                  key={doc.id}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(filters.doctorIds || []).includes(
                                      String(doc.id),
                                    )}
                                    onChange={() => handleDoctorFilterToggle(doc.id)}
                                  />
                                  <span>{doc.name}</span>
                                </label>
                              ))
                            )}
                          </div>
                          {selectedDoctorNames.length > 0 && (
                            <button
                              type="button"
                              className="multiselect-dropdown__clear"
                              onClick={() => handleFilterChange("doctorIds", [])}
                            >
                              {t("filters.reset", { ns: "common" })}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="page-block table-list">
                {loading && patients.length === 0 ? (
                  <div className="no-patients">{t("state.loading", { ns: "common" })}</div>
                ) : patients.length === 0 ? (
                  <div className="no-patients">{t("list.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colFio")}</th>
                        <th>{t("list.colPhone")}</th>
                        <th>{t("list.colDoctor")}</th>
                        <th>{t("list.colLastVisit")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map((patient) => (
                        <tr key={patient?.id?.value || patient?.id}>
                          <td>
                            <Link
                              to={`/patient/${patient?.id?.value}/profile`}
                              className="table-item-link">
                              <div className="table-item-name">
                                {patient?.fio?.value}
                              </div>
                            </Link>
                          </td>
                          <td>{patient?.phone?.value || "-"}</td>
                          <td>
                            {(patient?.doctors?.value || [])
                              .map((doc) => doc?.name)
                              .filter(Boolean)
                              .join(", ") ||
                              patient?.doctor?.value?.name ||
                              "-"}
                          </td>
                          <td>{formatDate(patient?.last_visit?.value)}</td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={3} className="summary-cell">
                          {t("list.found", { count: pagination.total || patients.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {/* Пагинация */}
                {patients.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("pageOf", { ns: "common", page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* === МОДАЛЬНОЕ ОКНО === */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("modal.addTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.fioLabel")}</Form.Label>
              <Form.Control
                type="text"
                name="fio"
                value={newPatient.fio}
                onChange={handleInputChange}
                placeholder={t("modal.fioPlaceholder")}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="mail"
                value={newPatient.mail}
                onChange={handleInputChange}
                placeholder="example@mail.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("list.colPhone")}</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={newPatient.phone}
                onChange={handleInputChange}
                placeholder="+7 (999) 123-45-67"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("modal.doctorLabel")}</Form.Label>
              <div className="patients-doctors-picker">
                {doctors.length === 0 ? (
                  <div className="patients-doctors-picker__empty">
                    {t("list.doctorsEmpty")}
                  </div>
                ) : (
                  doctors.map((doc) => (
                    <Form.Check
                      key={doc.id}
                      type="checkbox"
                      id={`new-patient-doctor-${doc.id}`}
                      label={doc.name}
                      checked={(newPatient.doctorIds || []).includes(
                        String(doc.id),
                      )}
                      onChange={() => handleNewPatientDoctorToggle(doc.id)}
                    />
                  ))
                )}
                <div className="patients-doctors-picker__hint">
                  {t("modal.doctorHint")}
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("modal.diagnosisLabel")}</Form.Label>
              <Form.Control
                as="textarea"
                name="diagnosis"
                value={newPatient.diagnosis}
                onChange={handleInputChange}
                placeholder={t("modal.diagnosisPlaceholder")}
                rows={5}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleModalClose}>
            {t("actions.cancel", { ns: "common" })}
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={handleSavePatient}
            disabled={
              !newPatient.fio.trim() ||
              (newPatient.doctorIds || []).length === 0
            }>
            {t("actions.save", { ns: "common" })}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Patients;
